import React, { useEffect, useState } from 'react';
import { GridRowParams } from '@material-ui/data-grid';
import { Checkbox, FormControlLabel, ListItem, Typography } from '@material-ui/core';
import { Route, Switch } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import _ from 'lodash';
import moment from 'moment';
import ActionsMenu from '../../components/other/ActionsMenu';
import LocationDetails from './LocationDetails';
import TextLink from '../../components/other/TextLink';
import { ChargingLocation } from '../../types/ChargingLocation';
import {
  LOCATION_DETAILS_TABS,
  locationDetailsTabParameters,
  SESSION_DETAILS_TABS,
  sessionDetailsTabParameters,
} from '../../utils/routing';
import { FormInputTypes } from '../../utils/constants';
import { drawerStore } from '../../state/drawerStore';
import { gridColumn } from '../../utils/gridUtils';
import { Api } from '../../api';
import { PAGES } from '../../components/main/MainComponent';
import credentialStore from '../../state/credentialStore';
import { VMPaginatedGrid } from '../../components/main/VMPaginatedGrid';
import { usePaginatedProvider } from '../../state/paginatedProviderStore';
import { ConnectorsList } from '../../components/other/Connectors';
import { ReactComponent as PrivateCompanyIcon } from '../../images/Locations/privateCompany.svg';
import { ReactComponent as PublicCompanyIcon } from '../../images/Locations/publicCompany.svg';

const locationCreateInputs = [
  { name: 'name', type: FormInputTypes.textField },
  { name: 'tags', type: FormInputTypes.multiAutocomplete },
  { name: 'searchAddress', type: FormInputTypes.googleAddressAutocomplete },
  { name: 'address', type: FormInputTypes.textField },
  { name: 'city', type: FormInputTypes.textField },
  { name: 'zip', type: FormInputTypes.textField },
  { name: 'country', type: FormInputTypes.textField },
  { name: 'longitude', type: FormInputTypes.disabledTextField },
  { name: 'latitude', type: FormInputTypes.disabledTextField },
];

const locationEditInputs = [
  { name: 'id', type: FormInputTypes.disabledTextField },
  ...locationCreateInputs,
  { name: 'status', type: FormInputTypes.switch },
];

const LocationsPage = observer(() => {
  const locationsProvider = usePaginatedProvider(undefined, undefined, false);
  const [sortableFields, setSortableFields] = useState([]);
  const [locationsSessionsTimes, setLocationsSessionsTimes] = useState([]);
  const [locationAccountTypes, setLocationAccountTypes] = useState([]);
  const [viziDriveOnly, setViziDriveOnly] = useState(true);
  useEffect(() => {
    if (credentialStore.isUserAdmin) {
      locationsProvider.setRequest((offset, sortBy, sortOrder) =>
        Api.charge
          .listLocations(
            { excludeRoaming: viziDriveOnly },
            { skip: offset, take: 20, sortBy, sortOrder },
          )
          .then((r) => {
            const locationIds = r?.data?.data?.map((location) => location.id);
            Api.charge.listOwnersForLocations(locationIds).then((response_) => {
              const ownerData = response_?.data?.data;
              const accountLocationMap = new Map(
                ownerData.map((account) => [account.accountId, account.locationId]),
              );
              const accountIds = ownerData.map((account) => account.accountId);
              Api.business.getAccountTypes(accountIds).then((res) => {
                const accountTypes = res?.data?.data;
                const combinedResult = accountTypes.reduce((acc, accountTypeInfo) => {
                  const locationId = accountLocationMap.get(accountTypeInfo.Account_id) as string;
                  if (locationId) {
                    acc[locationId] = {
                      accountId: accountTypeInfo.Account_id,
                      accountType: accountTypeInfo.Account_type,
                    };
                  }
                  return acc;
                }, {} as Record<string, { accountId: string; accountType: string }>);
                setLocationAccountTypes(combinedResult);
              });
            });
            Api.charge.listLocationLatestSessionsStartTimes(locationIds).then((_r) => {
              const responseData = _r?.data?.data;
              const momentConvertedTimesData = responseData.map((e) => ({
                ...e,
                latestStartedAt: moment.utc(e.latestStartedAt).local().format('DD.MM.YYYY HH:mm'),
              }));
              const data = _.mapValues(_.keyBy(momentConvertedTimesData, 'locationId'), (item) => ({
                latestStartedAt: item.latestStartedAt,
                latestFinishedAt: item.latestFinishedAt,
                connectorName: item.connectorName,
                sessionId: item.sessionId,
                status: item.status,
              }));
              setLocationsSessionsTimes(data);
              return _r;
            });
            return r;
          }),
      );
    } else {
      locationsProvider.setRequest((offset, sortBy, sortOrder) =>
        Api.charge.listLocationsForAccount(credentialStore.selectedAccount.id, {
          skip: offset,
          take: 20,
          sortBy,
          sortOrder,
        }),
      );
    }
    locationsProvider.setOnFulfilled((_data, _count, sortable) => {
      if (sortable) {
        setSortableFields(sortable);
      }
    });
    locationsProvider.setSort('name', 'ASC');
    // setSort only reloads when the sort itself changes, so trigger the fetch
    // explicitly here to also react to the ViziDrive-only filter toggling.
    locationsProvider.reloadResource();
  }, [credentialStore.selectedAccount, viziDriveOnly]);

  const onClickConfirmAdd = () => {
    const location = toJS(drawerStore.values) as Location;

    if (credentialStore.isUserAdmin) {
      Api.charge.createLocation(location).then(locationsProvider.reloadResource);
    } else {
      Api.charge
        .ownerCreateLocation(location, credentialStore.selectedAccount.id)
        .then(locationsProvider.reloadResource);
    }
  };

  const onClickConfirmEdit = () => {
    const location = drawerStore.values as ChargingLocation;

    Api.charge.patchLocation(location.id, location).then(locationsProvider.reloadResource);
  };

  const onClickCreateLocation = () => {
    drawerStore.openDrawer({
      label: 'Create location',
      inputs: locationCreateInputs,
      clickCallback: onClickConfirmAdd,
    });
  };

  const onClickEditLocation = (location: any) => {
    drawerStore.openDrawer({
      label: 'Edit location',
      inputs: locationEditInputs,
      clickCallback: onClickConfirmEdit,
    });

    drawerStore.setValues(location);
  };

  const onClickRemoveLocation = (location: any) => {
    Api.charge.deleteLocation(location.id).then(locationsProvider.reloadResource);
  };

  const chargerLocationActions = (params: GridRowParams) => (
    <ActionsMenu>
      <ListItem button onClick={() => onClickEditLocation(params.row)}>
        <Typography style={{ width: '100%' }}>Edit</Typography>
      </ListItem>
      <ListItem button onClick={() => onClickRemoveLocation(params.row)}>
        <Typography style={{ width: '100%' }}>Remove</Typography>
      </ListItem>
    </ActionsMenu>
  );

  const chargeLocationsColumns = [
    gridColumn('', {
      flex: 0.5,
      sortableFields,
      width: 60,
      renderCell: (params: GridRowParams) => {
        const locationType = locationAccountTypes[params.row.id];
        if (!locationType) {
          return null;
        }
        let IconComponent;
        switch (locationType.accountType) {
          case 'private':
            IconComponent = PrivateCompanyIcon;
            break;
          case 'company':
            IconComponent = PublicCompanyIcon;
            break;
          default:
            IconComponent = null;
        }
        return (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 'normal' }}>
            {IconComponent && <IconComponent style={{ marginBottom: '4px' }} />}
          </div>
        );
      },
    }),
    gridColumn('Location', {
      flex: 3,
      sortableFields,
      minWidth: 150,
      renderCell: (params: GridRowParams) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 'normal' }}>
          <TextLink
            to={locationDetailsTabParameters(params.row.id, LOCATION_DETAILS_TABS.OVERVIEW)}
            label={params.row.name}
            divStyle={{ marginBottom: '0.375rem' }}
          />
          <span
            style={{ color: 'gray', fontSize: '0.9rem' }}
          >{`${params.row.address}, ${params.row.zip}, ${params.row.city}`}</span>
        </div>
      ),
    }),
    gridColumn('connectors', {
      flex: 3,
      minWidth: 150,
      sortableFields,
      renderCell: (params: GridRowParams) => {
        const connectors = params.row.connectors || [];
        if (!connectors || connectors.length === 0) {
          return null;
        }
        const sortedConnectors = connectors.sort((a, b) => {
          const statusOrder = ['in_use', 'maintenance', 'available'];
          return statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
        });
        const numAvailableConnectors = sortedConnectors.filter(
          (connector) => connector.status === 'in_use',
        ).length;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 'normal' }}>
            <ConnectorsList connectors={sortedConnectors} />

            <div
              style={{
                marginTop: '0.375rem',
                marginLeft: '0.2rem',
                color: 'gray',
                fontSize: '0.8rem',
              }}
            >{`${numAvailableConnectors}/${connectors.length}`}</div>
          </div>
        );
      },
    }),
    gridColumn('lastSession', {
      flex: 2,
      minWidth: 150,
      sortableFields,
      renderCell: (params: GridRowParams) => {
        const latestSessionDetails = locationsSessionsTimes[params.row.id];
        if (!latestSessionDetails) {
          return null;
        }
        const connectorNamePart = latestSessionDetails.connectorName
          ? ` @ ${latestSessionDetails.connectorName}`
          : '';
        return (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 'normal' }}>
            <TextLink
              to={sessionDetailsTabParameters(
                latestSessionDetails.sessionId,
                SESSION_DETAILS_TABS.OVERVIEW,
              )}
              label={latestSessionDetails.latestStartedAt}
            />

            <div
              style={{ marginTop: '0.375rem' }}
            >{`${latestSessionDetails.status}${connectorNamePart}`}</div>
          </div>
        );
      },
    }),
    gridColumn('tags', {
      flex: 1,
      sortableFields,
      minWidth: 150,
      renderCell: (params: GridRowParams) => {
        const tagsArray = params.row.tags ? params.row.tags.split(' ') : [];
        return (
          <div>
            {tagsArray.map((tag: string, index: number) => (
              <span
                key={index}
                style={{
                  backgroundColor: '#00E8A8',
                  borderRadius: '4px',
                  padding: '0.2rem 0.6rem',
                  marginRight: '0.5rem',
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        );
      },
    }),
  ];

  if (credentialStore.canManageLocations()) {
    chargeLocationsColumns.push(
      gridColumn(' ', {
        width: 60,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params: GridRowParams) => chargerLocationActions(params),
      }),
    );
  }

  const filterCallback = (param?: string, val?: string) => {
    const urlParams: any = { excludeRoaming: viziDriveOnly };
    if (param && val) {
      urlParams[param] = val;
    }

    locationsProvider.setRequest(async (offset, sortBy, sortOrder) => {
      const locationResponse = await Api.charge.listLocations({
        ...urlParams,
        skip: offset,
        take: 20,
        sortBy,
        sortOrder,
      });
      const locationData = locationResponse?.data?.data || [];
      const locationIds = locationData.map((location) => location.id);

      const sessionsRes = await Api.charge.listLocationLatestSessionsStartTimes(locationIds);
      const sessionsMap = _.mapValues(_.keyBy(sessionsRes?.data?.data, 'locationId'), (item) => ({
        latestStartedAt: moment.utc(item.latestStartedAt).local().format('DD.MM.YYYY HH:mm'),
        latestFinishedAt: item.latestFinishedAt,
        connectorName: item.connectorName,
        sessionId: item.sessionId,
        status: item.status,
      }));
      setLocationsSessionsTimes(sessionsMap);
      return locationResponse;
    });

    locationsProvider.reloadResource();
  };

  const onViziDriveOnlyFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setViziDriveOnly(event.target.checked);
  };

  return (
    <VMPaginatedGrid
      label="Locations"
      count={locationsProvider.count}
      rows={locationsProvider.data}
      columns={chargeLocationsColumns}
      onClickAdd={credentialStore.canManageLocations() ? onClickCreateLocation : undefined}
      filterParamOptions={[
        { label: 'Name', value: 'name' },
        { label: 'Tags', value: 'tags' },
      ]}
      filterCallback={filterCallback}
      onPageOffsetChange={locationsProvider.onPageChange}
      onSortTypeChange={locationsProvider.setSort}
      defaultSortModel={{ field: 'name', sort: 'asc' }}
      rowHeight={60}
      additionalHeaderContent={
        credentialStore.isUserAdmin ? (
          <FormControlLabel
            control={
              <Checkbox
                checked={viziDriveOnly}
                onChange={onViziDriveOnlyFilterChange}
                color="primary"
              />
            }
            label="ViziDrive Locations only"
          />
        ) : undefined
      }
    />
  );
});

const LocationsPageWrapper = () => (
  <Switch>
    <Route path={`/${PAGES.Locations}/:locationid`} component={LocationDetails} />
    <Route path={`/${PAGES.Locations}`} component={LocationsPage} />
  </Switch>
);
export default LocationsPageWrapper;
